// ArtifactCard (TASK-742) — a creation result as a card. Chat used to emit
// OpenDocument for every generated document and navigate the RM off the dashboard;
// now creation renders this card (review before committing attention) and only an
// explicit open_document still navigates. Props come from the tool result payload —
// server-grounded, no self-fetch.
//
// One card core, three mounts (2026-08-05 deep-audit closure): the canvas
// registry renders the full ArtifactCard; WorkbenchRail renders the full card
// inline in the conversation for creation results; the task Artifacts section
// renders the shared `ArtifactIdentity` core inside its binding groups (the
// binding controls — role/withdraw/evidence — stay purpose-built there).
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  FileSpreadsheet,
  FileText,
  Presentation,
  User,
} from 'lucide-react';
import { toast } from 'sonner';
import { WidgetContainer } from '../WidgetContainer';
import { taskService } from '../../../services/taskService';
import { workbenchRoute } from '../../../utils/workbenchRoute';
import type { Task } from '../../../types/tasks';

export interface ArtifactCardProps {
  documentId?: string;
  title?: string | null;
  format?: string | null;
  version?: number | null;
  clientId?: string | null;
  /** A failed generation — renders the honest failure card (message, no actions). */
  error?: string | null;
  /** TASK-743 auto-attach: the task this generation ran under, when there was one. */
  taskId?: string | null;
  /**
   * TASK-743: `false` = the document was created but could NOT be attached to the task
   * (partial success — the document is saved and never lost); `true` = attached.
   */
  attached?: boolean;
}

// Nondisclosing on purpose: missing vs cross-account vs transient stay server-side.
const ATTACH_FAILED_COPY = 'Document created — it could not be attached to the task.';
const OPEN_STATUSES = new Set(['pending', 'in_progress', 'review']);

// Same lucide vocabulary the workbenches use for their toolbar format icons.
const FORMAT_META: Record<string, { label: string; Icon: typeof FileText }> = {
  docx: { label: 'Word document', Icon: FileText },
  xlsx: { label: 'Spreadsheet', Icon: FileSpreadsheet },
  pptx: { label: 'Presentation', Icon: Presentation },
  pdf: { label: 'PDF', Icon: FileText },
};

/** Shared format → {label, Icon} vocabulary for every artifact surface. */
export function formatMeta(format?: string | null): { label: string; Icon: typeof FileText } {
  return (
    FORMAT_META[(format ?? '').toLowerCase()] ?? {
      label: format ? format.toUpperCase() : 'Document',
      Icon: FileText,
    }
  );
}

/**
 * The shared card core: format icon + title (+ optional subtitle/client link).
 * Mounted by ArtifactCard (canvas + workbench rail) and by the task Artifacts
 * section's binding groups — one identity rendering on every surface.
 */
export function ArtifactIdentity({
  Icon,
  title,
  subtitle,
  clientId,
  titleClassName = 'truncate text-[14px] font-semibold text-foreground',
  children,
}: {
  Icon: typeof FileText;
  title: string;
  subtitle?: string | null;
  clientId?: string | null;
  titleClassName?: string;
  children?: React.ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <div className="flex items-start gap-3" data-testid="artifact-identity">
      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0">
        <p className={titleClassName}>{title}</p>
        {subtitle && <p className="text-[12px] text-muted-foreground">{subtitle}</p>}
        {clientId && (
          <button
            type="button"
            onClick={() => navigate(`/clients/${clientId}`)}
            className="mt-1 flex items-center gap-1 text-[12px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            <User className="h-3 w-3" aria-hidden />
            View client
          </button>
        )}
        {children}
      </div>
    </div>
  );
}

export function ArtifactCard({
  documentId,
  title,
  format,
  version,
  clientId,
  error,
  taskId,
  attached,
}: ArtifactCardProps) {
  const navigate = useNavigate();
  const [attachedNow, setAttachedNow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pickerTasks, setPickerTasks] = useState<Task[] | null>(null);
  // TASK-741: export-from-version — the card exports the version it announces.
  const [exporting, setExporting] = useState(false);
  const [exportedFrom, setExportedFrom] = useState<number | null>(null);

  const handleExport = async () => {
    if (!documentId || version == null) return;
    setExporting(true);
    try {
      const { exportDocumentVersion } = await import('../../../services/api/documentApi');
      const result = await exportDocumentVersion(documentId, version, 'same');
      setExportedFrom(result.source_document_version);
      toast.success(`Deliverable ready — exported from v${result.source_document_version}`);
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const isAttached = attached === true || attachedNow;
  const attachFailed = !!taskId && attached === false && !attachedNow;

  const attachTo = async (targetTaskId: string, failCopy: string) => {
    if (!documentId) return;
    setBusy(true);
    try {
      await taskService.attachTaskArtifact(targetTaskId, {
        artifact_type: 'document',
        artifact_id: documentId,
        artifact_version: version ?? undefined,
        role: 'output',
      });
      setAttachedNow(true);
      setPickerTasks(null);
    } catch {
      toast.error(failCopy);
    } finally {
      setBusy(false);
    }
  };

  const openPicker = async () => {
    setBusy(true);
    try {
      const data = await taskService.fetchTasks({ limit: 100 });
      // ponytail: one unfiltered fetch + client-side status filter — the list endpoint
      // takes a single status; three round-trips for a 20-row picker is not worth it.
      setPickerTasks(
        (data.tasks ?? []).filter((t) => OPEN_STATUSES.has(t.status)).slice(0, 20),
      );
    } catch {
      toast.error('Tasks could not be loaded.');
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <WidgetContainer source="Workbench" className="border-destructive">
        <div className="flex items-start gap-2" data-testid="artifact-card-error">
          <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" aria-hidden />
          <div>
            <p className="text-[13px] font-semibold text-foreground">
              {title ? `Could not create “${title}”` : 'Generation failed'}
            </p>
            <p className="mt-0.5 text-[13px] text-destructive">{error}</p>
          </div>
        </div>
      </WidgetContainer>
    );
  }

  const meta = formatMeta(format);

  return (
    <WidgetContainer source="Workbench">
      <div data-testid="artifact-card">
        <ArtifactIdentity
          Icon={meta.Icon}
          title={title || 'Untitled document'}
          subtitle={`${meta.label}${version != null ? ` · v${version}` : ''}`}
          clientId={clientId}
        />

        {attachFailed && (
          <div
            data-testid="artifact-card-attach-failed"
            className="mt-2 flex items-center gap-2 rounded-md bg-amber-50 px-2 py-1.5 text-[12px] text-amber-800"
          >
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>{ATTACH_FAILED_COPY}</span>
            <button
              type="button"
              disabled={busy}
              onClick={() => taskId && attachTo(taskId, ATTACH_FAILED_COPY)}
              className="ml-auto rounded border border-amber-300 px-2 py-0.5 font-medium hover:bg-amber-100 disabled:opacity-50"
            >
              Retry
            </button>
          </div>
        )}
        {isAttached && (
          <p className="mt-2 flex items-center gap-1.5 text-[12px] text-green-700">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Attached to task
          </p>
        )}

        {pickerTasks && !isAttached && (
          <div className="mt-2 rounded-md border border-border p-2">
            <p className="text-[11px] font-medium text-muted-foreground">Attach to an open task</p>
            {pickerTasks.length === 0 ? (
              <p className="mt-1 text-[12px] text-muted-foreground">No open tasks.</p>
            ) : (
              <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto">
                {pickerTasks.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        attachTo(t.id, 'The document could not be attached to the task.')
                      }
                      className="w-full truncate rounded px-1.5 py-1 text-left text-[12px] hover:bg-muted disabled:opacity-50"
                    >
                      {t.title}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="mt-3 flex items-center gap-2 border-t border-border pt-3">
          <button
            type="button"
            // ponytail: plain navigation for now — location-aware popup (stay on the
            // dashboard, open the focus popup) lands with TASK-717.
            // TASK-785: carry the task scope the card already holds — without `?task=`
            // the workbench rail opens empty (no origin thread, no resumed conversation).
            onClick={() => documentId && navigate(workbenchRoute(documentId, taskId))}
            className="rounded-md bg-primary px-3 py-1.5 text-[12px] font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Open in Workbench
          </button>
          <button
            type="button"
            disabled={!documentId || isAttached || busy}
            onClick={() => (pickerTasks ? setPickerTasks(null) : openPicker())}
            className="rounded-md border border-border px-3 py-1.5 text-[12px] font-medium text-foreground transition-colors hover:bg-muted disabled:text-muted-foreground disabled:opacity-50 disabled:hover:bg-transparent"
          >
            Add to task
          </button>
          <button
            type="button"
            disabled={!documentId || version == null || exporting}
            title={
              version == null
                ? 'Export needs a stored version'
                : 'Export this version as a deliverable (exact copy)'
            }
            onClick={() => void handleExport()}
            className="rounded-md border border-border px-3 py-1.5 text-[12px] font-medium text-foreground transition-colors hover:bg-muted disabled:text-muted-foreground disabled:opacity-50 disabled:hover:bg-transparent"
          >
            {exporting ? 'Exporting…' : exportedFrom != null ? `Exported v${exportedFrom} ✓` : 'Export'}
          </button>
        </div>
      </div>
    </WidgetContainer>
  );
}
